import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { useCallback, useState } from 'react';

import type { UseMutationResult } from '@tanstack/react-query';

import type { GenerateTextsPayload, GenerateTextsResponse } from '@/features/campaign-texts/types/campaign-texts.types';

import {
  defaultTextPicksForResult,
  patchBatchAfterRegenerate,
  type TextPickBatches,
} from '../shared/bulkEditAiTextPicks';
import type { CountryGenResult } from '../shared/multiCountryBulkEdit.types';

type GenMut = UseMutationResult<GenerateTextsResponse, Error, GenerateTextsPayload>;

export function useBulkEditAiTextRegen(p: {
  resultsRef: MutableRefObject<Record<string, CountryGenResult>>;
  textPicksRef: MutableRefObject<Record<string, TextPickBatches>>;
  genTexts: GenMut;
  setResultsByCountry: Dispatch<SetStateAction<Record<string, CountryGenResult>>>;
  setTextPicks: Dispatch<SetStateAction<Record<string, TextPickBatches>>>;
}) {
  const [regenTarget, setRegenTarget] = useState<{ ck: string; batchIdx: number } | null>(null);

  const regenerateUnselectedInBatch = useCallback(
    async (ck: string, batchIdx: number) => {
      const r = p.resultsRef.current[ck];
      const picks = p.textPicksRef.current[ck];
      if (!r) return;
      const batch = r.textBatches?.[batchIdx] ?? (batchIdx === 0 ? r.texts : []);
      const flags = picks?.[batchIdx] ?? picks?.[0] ?? batch.map(() => true);
      const idx = batch.map((_, i) => i).filter((i) => !flags[i]);
      if (idx.length === 0) return;
      const gId = r.groupIdsPerDrop?.[batchIdx] ?? r.textSourceGroupId ?? 'master';
      setRegenTarget({ ck, batchIdx });
      try {
        const data = await p.genTexts.mutateAsync({
          groupId: gId,
          countryKey: ck,
          amount: idx.length,
          translate: false,
        });
        p.setResultsByCountry((prev) => {
          const cur = prev[ck];
          if (!cur) return prev;
          return { ...prev, [ck]: patchBatchAfterRegenerate(cur, batchIdx, idx, data.texts, data.translations) };
        });
        p.setTextPicks((prev) => {
          const rows = [...(prev[ck] ?? defaultTextPicksForResult(r))];
          const row = [...(rows[batchIdx] ?? [])];
          for (const i of idx) if (i < row.length) row[i] = true;
          rows[batchIdx] = row;
          return { ...prev, [ck]: rows };
        });
      } finally {
        setRegenTarget(null);
      }
    },
    [p.genTexts, p.setResultsByCountry, p.setTextPicks, p.resultsRef, p.textPicksRef],
  );

  return { regenTarget, regenerateUnselectedInBatch };
}
